import {
  Button,
  Grid,
  Space,
  Tour,
  Typography,
  message,
} from 'antd';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ENABLE_AUTH } from '../../shared/utils/constants';
import { useAuthSession } from './AuthSessionProvider';
import { useGuardedNavigate } from './FormSafetyProvider';
import { fetchWorkspaceShowcase, updateWorkspaceShowcase } from '../../features/settings/api/queries';
import { settingsKeys } from '../../shared/api/cacheKeys';
import {
  buildWorkspaceShowcaseSteps,
  getPreferredShowcaseStartPath,
  resolveWorkspaceShowcaseTarget,
} from '../../shared/ui/layout/workspaceShowcaseRegistry';
import styles from './WorkspaceShowcaseProvider.module.css';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const WorkspaceShowcaseContext = createContext({
  startReplay: () => {},
  isRunning: false,
  isReplayAvailable: false,
});

const TARGET_RETRY_MS = 220;
const TARGET_MAX_ATTEMPTS = 6;

function buildFlowSteps(allSteps = [], moduleKeys = []) {
  const normalizedModuleKeys = Array.isArray(moduleKeys) ? moduleKeys : [];
  return allSteps.filter((step) => normalizedModuleKeys.includes(step.moduleKey));
}

function buildReplayModuleKeys(availableSteps = [], availableModuleKeys = []) {
  const normalizedAvailableModuleKeys = Array.isArray(availableModuleKeys)
    ? availableModuleKeys.filter(Boolean)
    : [];

  if (normalizedAvailableModuleKeys.length) {
    return normalizedAvailableModuleKeys;
  }

  return Array.from(new Set((Array.isArray(availableSteps) ? availableSteps : []).map((step) => step.moduleKey).filter(Boolean)));
}

function shouldMarkModuleComplete(steps = [], currentIndex = 0) {
  const currentStep = steps[currentIndex];
  const nextStep = steps[currentIndex + 1];
  if (!currentStep) {
    return false;
  }

  return !nextStep || nextStep.moduleKey !== currentStep.moduleKey;
}

function scrollShowcaseTargetIntoView(target, step) {
  if (!target || typeof target.scrollIntoView !== 'function') {
    return;
  }

  target.scrollIntoView({
    block: step?.needsSidebar ? 'center' : 'nearest',
    inline: 'nearest',
    behavior: 'auto',
  });
}

export function useWorkspaceShowcase() {
  return useContext(WorkspaceShowcaseContext);
}

export default function WorkspaceShowcaseProvider({
  children,
  enabled = true,
  navigationOpen = false,
  setNavigationOpen = () => {},
  showDesktopNavigation = true,
}) {
  const { session, isReady: isAuthReady } = useAuthSession();
  const guardedNavigate = useGuardedNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [isOpen, setIsOpen] = useState(false);
  const [flowSteps, setFlowSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeTourTarget, setActiveTourTarget] = useState(null);
  const autoStartHandledRef = useRef(false);
  const activeTargetRef = useRef(null);

  const showcaseEnabled =
    enabled &&
    ENABLE_AUTH &&
    isAuthReady &&
    Boolean(session?.accessToken && session?.tenantId && session?.user?.id);

  const sessionPermissions = useMemo(
    () => (Array.isArray(session?.permissions) ? session.permissions : []),
    [session?.permissions]
  );
  const preferredStartPath = useMemo(
    () =>
      getPreferredShowcaseStartPath(session?.role, sessionPermissions, {
        enableAuth: ENABLE_AUTH,
        authResolved: isAuthReady,
      }),
    [isAuthReady, session?.role, sessionPermissions]
  );

  const availableSteps = useMemo(
    () =>
      buildWorkspaceShowcaseSteps({
        role: session?.role,
        permissions: sessionPermissions,
        enableAuth: ENABLE_AUTH,
        authResolved: isAuthReady,
        startPath: preferredStartPath,
      }),
    [isAuthReady, preferredStartPath, session?.role, sessionPermissions]
  );

  const showcaseQuery = useQuery({
    queryKey: settingsKeys.showcase(),
    queryFn: fetchWorkspaceShowcase,
    enabled: showcaseEnabled,
    staleTime: 60_000,
  });

  const showcaseMutation = useMutation({
    mutationFn: updateWorkspaceShowcase,
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.showcase(), data);
    },
  });

  const persistShowcase = useCallback(
    async (payload) => {
      if (!showcaseEnabled) {
        return null;
      }

      return showcaseMutation.mutateAsync(payload);
    },
    [showcaseEnabled, showcaseMutation]
  );

  useEffect(() => {
    autoStartHandledRef.current = false;
    setIsOpen(false);
    setFlowSteps([]);
    setCurrentStepIndex(0);
    setActiveTourTarget(null);
  }, [session?.tenantId, session?.user?.id]);

  const goToStep = useCallback(
    (steps, index) => {
      const nextStep = steps[index];
      if (!nextStep) {
        return false;
      }

      if (nextStep.route && location.pathname !== nextStep.route) {
        const didNavigate = guardedNavigate(nextStep.route);
        if (didNavigate === false) {
          message.info('Finish or discard the current form changes before starting the guide.');
          return false;
        }
      }

      if (!showDesktopNavigation && isMobile) {
        setNavigationOpen(Boolean(nextStep.needsSidebar));
      }

      setCurrentStepIndex(index);
      return true;
    },
    [guardedNavigate, isMobile, location.pathname, setNavigationOpen, showDesktopNavigation]
  );

  const closeShowcase = useCallback(async () => {
    if (!isOpen) {
      return;
    }

    await persistShowcase({ action: 'dismiss' }).catch(() => null);
    setIsOpen(false);
    setFlowSteps([]);
    setCurrentStepIndex(0);
    setActiveTourTarget(null);
    if (!showDesktopNavigation && isMobile) {
      setNavigationOpen(false);
    }
  }, [isMobile, isOpen, persistShowcase, setNavigationOpen, showDesktopNavigation]);

  const startReplay = useCallback(async () => {
    const replayModuleKeys = buildReplayModuleKeys(
      availableSteps,
      showcaseQuery.data?.availableModuleKeys || []
    );
    const replaySteps = buildFlowSteps(
      availableSteps,
      replayModuleKeys
    );

    if (!replaySteps.length) {
      message.info('No guided workspace steps are available for this role right now.');
      return;
    }

    await persistShowcase({ action: 'replay_reset' }).catch(() => null);
    setFlowSteps(replaySteps);
    setIsOpen(true);
    setActiveTourTarget(null);
    goToStep(replaySteps, 0);
  }, [availableSteps, goToStep, persistShowcase, showcaseQuery.data?.availableModuleKeys]);

  useEffect(() => {
    if (!showcaseEnabled || !showcaseQuery.data || autoStartHandledRef.current) {
      return;
    }

    if (!showcaseQuery.data.autoStartEligible) {
      autoStartHandledRef.current = true;
      return;
    }

    const queuedModuleKeys = showcaseQuery.data.state?.queuedModuleKeys || [];
    const moduleKeys = queuedModuleKeys.length
      ? queuedModuleKeys
      : showcaseQuery.data.availableModuleKeys || [];
    const nextFlowSteps = buildFlowSteps(availableSteps, moduleKeys);

    if (!nextFlowSteps.length) {
      autoStartHandledRef.current = true;
      return;
    }

    autoStartHandledRef.current = true;
    persistShowcase({
      action: queuedModuleKeys.length ? 'continue' : 'start',
    }).catch(() => null);
    setFlowSteps(nextFlowSteps);
    setIsOpen(true);
    setActiveTourTarget(null);
    goToStep(nextFlowSteps, 0);
  }, [
    availableSteps,
    goToStep,
    persistShowcase,
    showcaseEnabled,
    showcaseQuery.data,
  ]);

  const currentStep = flowSteps[currentStepIndex] || null;

  useEffect(() => {
    if (!isOpen || !currentStep) {
      setActiveTourTarget(null);
      if (activeTargetRef.current) {
        activeTargetRef.current.removeAttribute('data-showcase-active');
        activeTargetRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let retryTimer = null;

    const resolveTarget = () => {
      if (cancelled) {
        return;
      }

      const target = resolveWorkspaceShowcaseTarget(currentStep);
      if (!target) {
        attempts += 1;
        if (attempts < TARGET_MAX_ATTEMPTS) {
          retryTimer = window.setTimeout(resolveTarget, TARGET_RETRY_MS);
          return;
        }

        const isLastStep = currentStepIndex >= flowSteps.length - 1;
        if (isLastStep) {
          persistShowcase({
            action: 'finish',
            stepKey: currentStep.stepKey,
            moduleKey: currentStep.moduleKey,
          }).catch(() => null);
          setIsOpen(false);
          setFlowSteps([]);
          setCurrentStepIndex(0);
          setActiveTourTarget(null);
          return;
        }

        if (shouldMarkModuleComplete(flowSteps, currentStepIndex)) {
          persistShowcase({
            action: 'module_complete',
            stepKey: currentStep.stepKey,
            moduleKey: currentStep.moduleKey,
          }).catch(() => null);
        } else {
          persistShowcase({
            action: 'step_complete',
            stepKey: currentStep.stepKey,
          }).catch(() => null);
        }
        goToStep(flowSteps, currentStepIndex + 1);
        return;
      }

      scrollShowcaseTargetIntoView(target, currentStep);

      if (activeTargetRef.current && activeTargetRef.current !== target) {
        activeTargetRef.current.removeAttribute('data-showcase-active');
      }

      target.setAttribute('data-showcase-active', 'true');
      activeTargetRef.current = target;
      setActiveTourTarget(target);
    };

    const timer = window.setTimeout(resolveTarget, 280);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (activeTargetRef.current) {
        activeTargetRef.current.removeAttribute('data-showcase-active');
        activeTargetRef.current = null;
      }
      setActiveTourTarget(null);
    };
  }, [currentStep, currentStepIndex, flowSteps, goToStep, isOpen, persistShowcase]);

  const handleNext = useCallback(async () => {
    if (!currentStep) {
      return;
    }

    const isLastStep = currentStepIndex >= flowSteps.length - 1;
    if (isLastStep) {
      await persistShowcase({
        action: 'finish',
        stepKey: currentStep.stepKey,
        moduleKey: currentStep.moduleKey,
      }).catch(() => null);
      setIsOpen(false);
      setFlowSteps([]);
      setCurrentStepIndex(0);
      setActiveTourTarget(null);
      if (!showDesktopNavigation && isMobile) {
        setNavigationOpen(false);
      }
      return;
    }

    if (shouldMarkModuleComplete(flowSteps, currentStepIndex)) {
      await persistShowcase({
        action: 'module_complete',
        stepKey: currentStep.stepKey,
        moduleKey: currentStep.moduleKey,
      }).catch(() => null);
    } else {
      await persistShowcase({
        action: 'step_complete',
        stepKey: currentStep.stepKey,
      }).catch(() => null);
    }

    goToStep(flowSteps, currentStepIndex + 1);
  }, [
    currentStep,
    currentStepIndex,
    flowSteps,
    goToStep,
    isMobile,
    persistShowcase,
    setNavigationOpen,
    showDesktopNavigation,
  ]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex <= 0) {
      return;
    }

    goToStep(flowSteps, currentStepIndex - 1);
  }, [currentStepIndex, flowSteps, goToStep]);

  const desktopSteps = useMemo(() => {
    return flowSteps.map((step, index) => ({
      title: step.title,
      description: step.description,
      target: () =>
        step.stepKey === currentStep?.stepKey
          ? activeTourTarget
          : resolveWorkspaceShowcaseTarget(step),
      placement: step.needsSidebar ? 'right' : 'bottom',
      prevButtonProps: {
        onClick: handlePrevious,
        children: 'Back',
      },
      nextButtonProps: {
        onClick: handleNext,
        children: index === flowSteps.length - 1 ? 'Finish' : 'Next',
      },
    }));
  }, [activeTourTarget, currentStep?.stepKey, flowSteps, handleNext, handlePrevious]);

  const contextValue = useMemo(
    () => ({
      startReplay,
      isRunning: isOpen,
      isReplayAvailable: showcaseEnabled && !showcaseQuery.isLoading,
    }),
    [isOpen, showcaseEnabled, showcaseQuery.isLoading, startReplay]
  );

  return (
    <WorkspaceShowcaseContext.Provider value={contextValue}>
      {children}
      {!isMobile ? (
        <Tour
          open={isOpen && flowSteps.length > 0 && Boolean(activeTourTarget)}
          onClose={closeShowcase}
          current={currentStepIndex}
          steps={desktopSteps}
          mask={{ color: 'rgba(6, 16, 24, 0.58)' }}
          rootClassName={styles.desktopTour}
          type="primary"
          indicatorsRender={(current, total) => (
            <span className={styles.indicator}>
              {current + 1} / {total}
            </span>
          )}
          actionsRender={(originNode, info) => (
            <div className={styles.desktopActions}>
              <Button type="text" onClick={closeShowcase}>
                Skip
              </Button>
              <span className={styles.desktopActionMeta}>
                Step {info.current + 1} of {info.total}
              </span>
              {originNode}
            </div>
          )}
        />
      ) : null}
      {isMobile && isOpen && currentStep ? (
        <div className={styles.mobileSheet} data-testid="workspace-showcase-mobile">
          <div className={styles.mobileSheetHeader}>
            <Text className={styles.mobileKicker}>Workspace guide</Text>
            <Button type="text" size="small" onClick={closeShowcase}>
              Skip
            </Button>
          </div>
          <Title level={4} className={styles.mobileTitle}>
            {currentStep.title}
          </Title>
          <Text className={styles.mobileDescription}>{currentStep.description}</Text>
          <Space className={styles.mobileProgress}>
            <span className={styles.indicator}>
              {currentStepIndex + 1} / {flowSteps.length}
            </span>
            {currentStep.needsSidebar ? (
              <span className={styles.mobileHint}>Navigation is opened for this step.</span>
            ) : null}
          </Space>
          <div className={styles.mobileActions}>
            <Button onClick={handlePrevious} disabled={currentStepIndex === 0}>
              Back
            </Button>
            <Button type="primary" onClick={handleNext}>
              {currentStepIndex === flowSteps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      ) : null}
    </WorkspaceShowcaseContext.Provider>
  );
}
